// Property location via the existing server-side GovMap proxies (no Google
// Places — that's browser-referrer-restricted). /api/search is GovMap
// autocomplete; /api/geocoding?action=parcel returns a parcel polygon by
// gush/chelka. Both run server-side, so this works on localhost too.
import proj4 from 'proj4';
import apiConfig from '../../../config/api';

const BASE = apiConfig.apiBaseUrl || '';

// Address points come back in EPSG:3857 (GovMap autocomplete), parcel centers
// in EPSG:2039 (ITM). Define both so we can put them on a common datum.
proj4.defs('EPSG:2039', '+proj=tmerc +lat_0=31.73439361111111 +lon_0=35.20451694444445 +k=1.0000067 +x_0=219529.584 +y_0=626907.39 +ellps=GRS80 +towgs84=-48,55,52,0,0,0,0 +units=m +no_defs');
proj4.defs('EPSG:3857', '+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +wktext  +no_defs');

function toLatLng(pt, fromEpsg) {
  const [lng, lat] = proj4(fromEpsg, 'EPSG:4326', [pt.x, pt.y]);
  return { lat, lng };
}

function haversineMeters(a, b) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

// Distance in meters between an address point (EPSG:3857) and a parcel center
// (EPSG:2039). Used to flag an address ↔ gush/chelka mismatch. Null if either
// is missing or a projection fails.
export function metersBetweenAddrParcel(addr3857, parcel2039) {
  if (!addr3857 || !parcel2039) return null;
  try {
    return haversineMeters(toLatLng(addr3857, 'EPSG:3857'), toLatLng(parcel2039, 'EPSG:2039'));
  } catch {
    return null;
  }
}

// Detect a GovMap point's projection (3857 for address points, 2039 for parcel
// centers) and convert to WGS84 {lat,lng}.
export function pointToLatLng(p) {
  if (!p || typeof p.x !== 'number' || typeof p.y !== 'number') return null;
  const epsg = p.x > 3_000_000 ? 'EPSG:3857' : (p.x > 100_000 && p.x < 400_000 ? 'EPSG:2039' : null);
  if (!epsg) return null;
  try { return toLatLng(p, epsg); } catch { return null; }
}

// Convert a GovMap point to EPSG:2039 (ITM) {x,y} — the datum the planning tools
// (rights-autofill, plan/permit lookups) expect. Address points come in 3857;
// parcel centers are already 2039 and pass through unchanged.
export function pointToItm(p) {
  if (!p || typeof p.x !== 'number' || typeof p.y !== 'number') return null;
  try {
    if (p.x > 3_000_000) { const [x, y] = proj4('EPSG:3857', 'EPSG:2039', [p.x, p.y]); return { x, y }; }
    return { x: p.x, y: p.y };
  } catch { return null; }
}

// Fetch a Street View image for {lat,lng} at thumbnail size via the signed proxy
// and return it as a JPEG data URL (or null if there's no imagery / it failed).
// Stored on the project so the gallery never re-calls Google.
export async function streetViewThumb(lat, lng) {
  if (typeof lat !== 'number' || typeof lng !== 'number') return null;
  const params = new URLSearchParams({
    type: 'streetview',
    size: '480x288',
    location: `${lat},${lng}`,
    fov: '80',
    pitch: '6',
    source: 'outdoor',
    radius: '60',
    autoheading: '1',
  });
  try {
    const res = await fetch(`${BASE}/api/google-static?${params.toString()}`);
    if (!res.ok) return null;
    const blob = await res.blob();
    // The proxy returns an SVG placeholder when there's no Street View imagery.
    if (!blob.type || !blob.type.startsWith('image/') || blob.type.includes('svg')) return null;
    return await new Promise((resolve) => {
      const r = new FileReader();
      r.onload = () => resolve(typeof r.result === 'string' ? r.result : null);
      r.onerror = () => resolve(null);
      r.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

function parsePoint(shape) {
  // GovMap returns e.g. "POINT(3870580.91 3773480.43)" in EPSG:3857.
  const m = /POINT\(([-\d.]+)\s+([-\d.]+)\)/.exec(shape || '');
  if (!m) return null;
  return { x: Number(m[1]), y: Number(m[2]) };
}

// GovMap city tokens come back mangled (spaces stripped + abbreviations like
// "ת.איפו" / "ת\"א"). Normalize the Tel Aviv-Yafo variants; leave others as-is.
function normalizeCity(c) {
  const raw = String(c || '').trim();
  const compact = raw.replace(/["'.׳״\s]/g, '');
  if (/^(תלאביב(יפו)?|תאיפו|תא)$/.test(compact)) return 'תל אביב-יפו';
  return raw;
}

// Fallback for results whose id lacks structured parts: split the human text
// ("<street> <houseNo> <city>") on the house-number token.
function parseAddressText(text) {
  const tokens = String(text || '').trim().split(/\s+/).filter(Boolean);
  const numIdx = tokens.findIndex((t) => /^\d+[א-ת]?$/.test(t));
  if (numIdx === -1) return { street: tokens.join(' '), houseNo: '', city: '' };
  return {
    street: tokens.slice(0, numIdx).join(' '),
    houseNo: tokens[numIdx],
    city: tokens.slice(numIdx + 1).join(' '),
  };
}

// Split a suggestion into clean street / houseNo / city so each fills only its
// own field. Structured ids are authoritative:
//   address → "address|ADDR|<n>|<street>|<houseNo>|<city>"
//   street  → "street|STREET_MID_POINT|<n>|<street>|<city>"  (no house number)
function parseSuggestion(r) {
  const coord = parsePoint(r.shape);
  const parts = String(r.id || '').split('|');
  let street = '';
  let houseNo = '';
  let city = '';
  if (r.type === 'address' && parts.length >= 6) {
    street = parts[3] || '';
    houseNo = parts[4] || '';
    city = parts[5] || '';
  } else if (r.type === 'street' && parts.length >= 5) {
    street = parts[3] || '';
    city = parts[4] || '';
  }
  if (!street) {
    const fromText = parseAddressText(r.originalText || r.text);
    street = fromText.street;
    houseNo = houseNo || fromText.houseNo;
    city = city || fromText.city;
  }
  city = normalizeCity(city);
  // A clean label for the dropdown (GovMap's own text shows the mangled city).
  const label = [street, houseNo, city].filter(Boolean).join(' ') || r.text;
  return { id: r.id, text: label, type: r.type, coord, street, houseNo, city };
}

export async function searchAddress(text) {
  const q = String(text || '').trim();
  if (q.length < 2) return [];
  const res = await fetch(`${BASE}/api/search`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ searchText: q, language: 'he', maxResults: 8 }),
  });
  if (!res.ok) throw new Error(`address search failed: ${res.status}`);
  const data = await res.json();
  return (data.results || [])
    .map(parseSuggestion)
    .filter((s) => s.type === 'address' || s.type === 'street');
}

export async function parcelByGushHelka(gush, helka) {
  const g = String(gush || '').trim();
  const h = String(helka || '').trim();
  if (!g || !h) return null;
  const res = await fetch(`${BASE}/api/geocoding?action=parcel&gush=${encodeURIComponent(g)}&helka=${encodeURIComponent(h)}`);
  if (!res.ok) throw new Error(`parcel lookup failed: ${res.status}`);
  const data = await res.json();
  const feat = (data.features || [])[0];
  const geom = feat?.geometry;
  if (!geom) return null;
  // GeoJSON (MultiPolygon|Polygon, EPSG:2039) → ArcGIS-style rings for LocationMap.
  const rings = geom.type === 'MultiPolygon' ? geom.coordinates[0] : geom.coordinates;
  const outer = (rings && rings[0]) || [];
  if (outer.length < 3) return null;
  const cx = outer.reduce((a, p) => a + p[0], 0) / outer.length;
  const cy = outer.reduce((a, p) => a + p[1], 0) / outer.length;
  return {
    geometry: { rings, spatialReference: { wkid: 2039 } },
    center: { x: cx, y: cy },
  };
}
